/**
 * Format a scraped price for display. Shows the native scraped currency (the actual price
 * on the brand's site); the USD-normalized value is for search, not display.
 * Whole amounts render without decimals ($315), fractional with two ($29.99).
 * Returns null when there's no amount, so callers can conditionally render.
 */
export function formatPrice(amount?: number | string | null, currency?: string | null): string | null {
  // Postgres NUMERIC round-trips through PostgREST as a JSON string ("490.00"), so a saved item's
  // price_amount is a string while a freshly-scraped one is a number. Coerce, and reject
  // empty/blank strings (Number('') === 0 would otherwise render a bogus "$0").
  if (typeof amount === 'string' && amount.trim() === '') return null
  const n = typeof amount === 'string' ? Number(amount) : amount
  if (n == null || Number.isNaN(n)) return null

  const cur = (currency || '').toUpperCase()
  const fractionDigits = Number.isInteger(n) ? 0 : 2

  if (/^[A-Z]{3}$/.test(cur)) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: cur,
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }).format(n)
    } catch {
      // Unknown/invalid ISO code — fall through to plain formatting
    }
  }

  const s = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(n)
  return cur ? `${s} ${cur}` : s
}
